import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  SunDim, House, Users, FolderOpen, Bell, LockKeyOpen, SignOut, UserPlus, ArrowLeft, Kanban, MapPinLine, Truck,
  GearSix, CaretDown, DotsThreeOutline,
} from "@phosphor-icons/react";
import { api, ROLE_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { InstallAppButton } from "./InstallAppButton";
import { PushAlertButton } from "./PushAlertButton";

const slug = (s) => s.toLowerCase().replace(/\s/g, "-");

const buildNav = (user) => {
  if (user.role === "warehouse") return [{ to: "/", label: "Dispatch", short: "Dispatch", icon: Truck }];
  const perms = user.permissions || [];
  return [
    { to: "/", label: "Dashboard", short: "Home", icon: House },
    { to: "/customers", label: "Customer Files", short: "Customers", icon: FolderOpen },
    { to: "/work", label: "Work Hub", short: "Work", icon: Kanban },
    ...(user.role === "field_officer" ? [] : [{ to: "/inspections", label: "Inspections", short: "Sites", icon: MapPinLine }]),
    { to: "/notifications", label: "Notifications", short: "Alerts", icon: Bell },
    ...(perms.includes("create_customer") ? [{ to: "/customers/new", label: "New Customer", short: "New", icon: UserPlus }] : []),
    { to: "/edit-requests", label: "Edit Requests", short: "Requests", icon: LockKeyOpen },
    ...(perms.includes("manage_users") ? [{ to: "/users", label: "Team Users", short: "Team", icon: Users }] : []),
  ];
};

const Avatar = ({ name, className = "" }) => (
  <span className={`rounded-full bg-gradient-to-br from-brand-400 to-brand-700 text-white font-heading font-bold flex items-center justify-center shrink-0 ${className}`}>
    {(name || "?").slice(0, 1).toUpperCase()}
  </span>
);

const Brand = ({ dark }) => (
  <div className="flex items-center gap-2.5">
    <span className="w-9 h-9 rounded-xl bg-brand-500 text-white flex items-center justify-center shadow-lg shadow-brand-500/30"><SunDim size={22} weight="bold" /></span>
    <div className="leading-none">
      <div className={`font-heading font-extrabold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}>Anuj Solar</div>
      <div className={`text-[9px] uppercase tracking-[0.22em] font-bold mt-1 ${dark ? "text-brand-300" : "text-brand-700"}`}>Workflow ERP</div>
    </div>
  </div>
);

const SettingsBlock = ({ dark }) => (
  <div className={`space-y-1 rounded-xl p-1.5 ${dark ? "bg-white" : "bg-slate-50 ring-1 ring-slate-200"}`} data-testid="settings-panel">
    <ChangePasswordDialog />
    <InstallAppButton />
    <PushAlertButton />
  </div>
);

function Sidebar({ nav, user, path, onLogout }) {
  const [open, setOpen] = useState(false);
  return (
    <aside className="hidden md:flex w-64 bg-ink-950 flex-col fixed inset-y-0 z-30">
      <div className="h-[68px] px-5 flex items-center border-b border-white/[0.06]"><Brand dark /></div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-0.5">
        <div className="px-3 pb-2 text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Workspace</div>
        {nav.map((n) => {
          const active = path === n.to;
          return (
            <Link key={n.to} to={n.to} data-testid={`nav-${slug(n.label)}`}
              className={`group relative flex items-center gap-3 px-3 h-10 rounded-lg text-[13.5px] font-medium transition-colors ${active ? "bg-brand-500/15 text-brand-300" : "text-slate-400 hover:bg-white/[0.04] hover:text-slate-100"}`}>
              {active && <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-full bg-brand-400" />}
              <n.icon size={19} weight={active ? "fill" : "regular"} /> {n.label}
            </Link>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/[0.06] space-y-2">
        <div className="flex items-center gap-2.5 px-2 py-1.5">
          <Avatar name={user.name} className="w-9 h-9 text-sm" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-white truncate">{user.name}</div>
            <div className="text-[11px] text-slate-400 truncate">{ROLE_LABELS[user.role]}</div>
          </div>
          <button type="button" aria-label="Settings" title="Settings" data-testid="settings-toggle" onClick={() => setOpen((v) => !v)}
            className="w-8 h-8 rounded-lg text-slate-400 hover:bg-white/[0.06] hover:text-white flex items-center justify-center transition-colors">
            <GearSix size={17} className={`transition-transform ${open ? "rotate-90" : ""}`} />
          </button>
          <button type="button" aria-label="Sign out" title="Sign out" data-testid="logout-button" onClick={onLogout}
            className="w-8 h-8 rounded-lg text-slate-400 hover:bg-rose-500/15 hover:text-rose-300 flex items-center justify-center transition-colors">
            <SignOut size={17} />
          </button>
        </div>
        {open && <div className="animate-in fade-in slide-in-from-bottom-1 duration-150"><SettingsBlock dark /></div>}
      </div>
    </aside>
  );
}

function BottomBar({ nav, path, unread, onMore }) {
  const main = nav.slice(0, 4);
  const moreActive = !main.some((n) => n.to === path) && nav.slice(4).some((n) => n.to === path);
  const Tab = ({ active, icon: Icon, label, badge, ...rest }) => (
    <button type="button" {...rest} className={`tap relative flex-1 flex flex-col items-center justify-center gap-1 h-14 transition-colors ${active ? "text-brand-700" : "text-slate-500"}`}>
      <span className={`relative flex items-center justify-center w-12 h-7 rounded-full transition-colors ${active ? "bg-brand-100" : ""}`}>
        <Icon size={21} weight={active ? "fill" : "regular"} />
        {badge > 0 && <span className="absolute -top-0.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center">{badge > 99 ? "99+" : badge}</span>}
      </span>
      <span className={`text-[10.5px] leading-none ${active ? "font-bold" : "font-medium"}`}>{label}</span>
    </button>
  );
  const navigate = useNavigate();
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-xl border-t border-slate-200 pb-safe shadow-[0_-8px_24px_-12px_rgba(15,23,42,0.15)]" data-testid="bottom-tab-bar">
      <div className="flex px-1">
        {main.map((n) => (
          <Tab key={n.to} active={path === n.to} icon={n.icon} label={n.short} badge={n.to === "/notifications" ? unread : 0}
            data-testid={`tab-nav-${slug(n.short)}`} onClick={() => navigate(n.to)} />
        ))}
        <Tab active={moreActive} icon={DotsThreeOutline} label="More" data-testid="mobile-menu-button" onClick={onMore} />
      </div>
    </nav>
  );
}

function MoreSheet({ open, setOpen, nav, user, path, onLogout }) {
  const rest = nav.slice(4);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="bottom" className="rounded-t-3xl px-4 pt-3 pb-safe max-h-[85vh] overflow-y-auto" data-testid="more-sheet">
        <div className="mx-auto w-10 h-1.5 rounded-full bg-slate-200 mb-4" />
        <SheetHeader className="text-left">
          <SheetTitle className="flex items-center gap-3">
            <Avatar name={user.name} className="w-11 h-11 text-base" />
            <span className="leading-tight"><span className="block text-base">{user.name}</span><span className="block text-xs font-medium text-slate-500">{ROLE_LABELS[user.role]}</span></span>
          </SheetTitle>
          <SheetDescription className="sr-only">More pages and settings</SheetDescription>
        </SheetHeader>
        {rest.length > 0 && (
          <div className="grid grid-cols-4 gap-2 mt-5">
            {rest.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`nav-${slug(n.label)}`}
                className={`tap flex flex-col items-center gap-1.5 py-3 rounded-2xl transition-colors ${path === n.to ? "bg-brand-50 text-brand-700" : "text-slate-700 active:bg-slate-100"}`}>
                <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${path === n.to ? "bg-brand-500 text-white" : "bg-slate-100"}`}><n.icon size={21} /></span>
                <span className="text-[11px] font-semibold text-center leading-tight">{n.short}</span>
              </Link>
            ))}
          </div>
        )}
        <div className="mt-5 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 px-1 mb-2">Settings</div>
        <SettingsBlock />
        <button type="button" data-testid="logout-button-mobile" onClick={onLogout}
          className="tap mt-3 mb-2 w-full h-12 rounded-xl bg-rose-50 text-rose-600 font-semibold flex items-center justify-center gap-2 active:bg-rose-100">
          <SignOut size={18} /> Sign out
        </button>
      </SheetContent>
    </Sheet>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [more, setMore] = useState(false);
  const path = location.pathname;
  const nav = buildNav(user);
  const isWarehouse = user.role === "warehouse";

  useEffect(() => {
    if (isWarehouse) return undefined;
    const load = () => api.get("/notifications/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [path, isWarehouse]);
  useEffect(() => { setMore(false); window.scrollTo(0, 0); }, [path]);

  const onLogout = async () => { await logout(); navigate("/login"); };
  const title = (nav.find((n) => n.to === path) || {}).label || "Details";
  const isRoot = nav.some((n) => n.to === path);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar nav={nav} user={user} path={path} onLogout={onLogout} />
      <div className="md:ml-64 flex flex-col min-w-0 min-h-screen">
        <header className="h-14 md:h-[68px] sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-slate-200/70 flex items-center gap-2 px-3 sm:px-6">
          {!isRoot ? (
            <button type="button" data-testid="back-button" title="Back" aria-label="Back"
              onClick={() => (window.history.length > 2 ? navigate(-1) : navigate("/"))}
              className="tap w-10 h-10 -ml-1 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors active:scale-90">
              <ArrowLeft size={21} weight="bold" />
            </button>
          ) : <span className="md:hidden"><SunDim size={26} weight="bold" className="text-brand-600" /></span>}
          <div className="min-w-0 flex-1">
            <div className="font-heading font-bold text-[16px] md:text-lg text-slate-900 leading-tight truncate" data-testid="header-page-title">{title}</div>
            <div className="text-[11px] text-slate-500 font-medium hidden md:block">{new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</div>
          </div>
          {!isWarehouse && (
            <Link to="/notifications" data-testid="notifications-bell" aria-label="Notifications" title="Notifications"
              className="tap relative w-10 h-10 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors">
              <Bell size={21} />
              {unread > 0 && (
                <span data-testid="unread-badge" className="absolute top-1 right-1 bg-rose-500 text-white text-[9px] font-bold rounded-full min-w-[17px] h-[17px] flex items-center justify-center px-1 ring-2 ring-white">{unread}</span>
              )}
            </Link>
          )}
          <span className="hidden md:block"><Avatar name={user.name} className="w-9 h-9 text-sm" /></span>
        </header>
        <main className="flex-1 px-4 pt-4 pb-28 sm:px-6 sm:pt-6 md:pb-10">{children}</main>
      </div>
      <BottomBar nav={nav} path={path} unread={unread} onMore={() => setMore(true)} />
      <MoreSheet open={more} setOpen={setMore} nav={nav} user={user} path={path} onLogout={onLogout} />
    </div>
  );
}
